// OnboardingModal
// Runs the onboarding flow as an overlay, so the Matches feed's completion
// prompt can send the user straight into the step that fills the gap without
// navigating away from the feed they were scrolling.

import React, { useCallback, useEffect, useRef } from 'react';
import { OnboardingContainer } from './OnboardingContainer';
import { OnboardingStepId } from '../../types/onboarding';
import {
  ModalBackdrop,
  ModalPanel,
  ModalCloseButton,
  ModalBody,
} from '../../styles/onboarding/OnboardingModal.styles';

interface OnboardingModalProps {
  isOpen: boolean;
  /** Step to open on — normally the one that collects the missing field. */
  initialStepId?: OnboardingStepId;
  /**
   * Fired on close by any route (×, backdrop, Escape) and on completion.
   * Hosts should re-read completeness here: the user may have filled fields in.
   */
  onClose: () => void;
}

export const OnboardingModal: React.FC<OnboardingModalProps> = ({
  isOpen,
  initialStepId,
  onClose,
}) => {
  // Saves whatever step is on screen. Registered by the flow itself, because
  // only it knows which step that is.
  const persistRef = useRef<(() => Promise<void>) | null>(null);

  // CLOSING IS A WAY OF FINISHING, NOT OF CANCELLING. This modal exists to
  // fill one gap; someone who types their answer and hits × has done the thing
  // we asked. Persistence used to run only on "Next", so that answer was
  // silently discarded. Every close route goes through here.
  const closeWithSave = useCallback(async () => {
    try {
      await persistRef.current?.();
    } catch {
      // A failed save must still let the user out of the modal.
    }
    onClose();
  }, [onClose]);
  // Lock background scroll while open.
  useEffect(() => {
    if (!isOpen) return;
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = previous; };
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        closeWithSave();
      }
    };
    window.addEventListener('keydown', handleKeyDown, true);
    return () => window.removeEventListener('keydown', handleKeyDown, true);
  }, [isOpen, closeWithSave]);

  if (!isOpen) return null;

  return (
    <ModalBackdrop
      onClick={(e) => { if (e.target === e.currentTarget) closeWithSave(); }}
      role="dialog"
      aria-modal="true"
      aria-label="Complete your profile"
    >
      <ModalPanel>
        <ModalCloseButton onClick={closeWithSave} aria-label="Close setup">
          ×
        </ModalCloseButton>
        <ModalBody>
          {/* Remounted per open (key on the step) so the flow re-reads the
              backend and lands on the requested step each time. */}
          <OnboardingContainer
            key={initialStepId ?? 'default'}
            initialStepId={initialStepId}
            onFinish={onClose}
            onRegisterPersist={(persist) => { persistRef.current = persist; }}
          />
        </ModalBody>
      </ModalPanel>
    </ModalBackdrop>
  );
};
